import React from 'react';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Box, Collapse, List, ListItemButton, ListItemIcon, ListItemText, Paper } from '@mui/material';

import { ParaLogo } from '@paragon/client-tools';
import { PdfPage } from '@paragon/contract-library';

import { SanitizedDocument } from '../../store/documents';

interface Props {
  readonly documents: readonly SanitizedDocument[];
  readonly onDocumentClick: (id: number) => void;
  readonly selectedDocument: SanitizedDocument | undefined;
  readonly withPages?: boolean;
  readonly selectedPage?: PdfPage | undefined;
  readonly totalNumberOfPdfs?: number;
  readonly totalNumberOfPagesInCurrentPdf?: number;
  readonly onPageClick?: (documentId: number, pageNumber: number) => void;
}

interface State {
  readonly expandedDropdowns: { readonly [documentId: number]: boolean };
}

export default class DocumentQueueList extends React.Component<Props, State> {
  state: State = {
    expandedDropdowns: Object.fromEntries(this.props.documents.map((document) => [document.id, false])), // List of document IDs that are expanded
  };

  componentDidUpdate(prevProps: Props) {
    if (
      this.props.selectedDocument &&
      prevProps.selectedDocument &&
      this.props.selectedDocument.id !== prevProps.selectedDocument.id
    ) {
      // FIXME: if material ui provided a way to get a ref for a list's container, that would be a better approach
      const container = document.getElementById('document-queue')!;
      const item = document.getElementById(`document-queue-${this.props.selectedDocument.id}`)!;

      const containerTop = container.scrollTop;
      const containerBottom = containerTop + container.clientHeight;

      const itemTop = item.offsetTop;
      const itemBottom = itemTop + item.clientHeight;

      if (itemTop < containerTop) {
        container.scrollTop -= containerTop - itemTop;
      } else if (itemBottom > containerBottom) {
        container.scrollTop += itemBottom - containerBottom;
      }
    }
  }

  onDocumentClick = (documentId: number) => {
    if (this.props.onDocumentClick) {
      this.props.onDocumentClick(documentId);
    }
  };

  onPageClick = (documentId: number, pageNumber: number) => {
    if (this.props.onPageClick) {
      this.props.onPageClick(documentId, pageNumber);
    }
  };

  handleShowHidePagesClick = (event: React.MouseEvent<SVGSVGElement>, documentId: number) => {
    event.stopPropagation();

    this.setState((prevState) => {
      const expandedDropdowns = {
        ...prevState.expandedDropdowns,
        [documentId]: !prevState.expandedDropdowns[documentId],
      };
      return { expandedDropdowns };
    });
  };

  render() {
    const listItems = this.props.documents.map((document) => {
      const documentIsSelected =
        this.props.selectedDocument &&
        document.id === this.props.selectedDocument.id &&
        (!this.props.withPages || (this.props.selectedPage && this.props.selectedPage.pageNumber === 0));

      let listSubItems;
      if (this.props.withPages) {
        listSubItems = document.pdfPages
          .filter((pdfPage) => pdfPage.pageNumber > 0)
          .toSorted((aPage, bPage) => (aPage.pageNumber > bPage.pageNumber ? 1 : -1))
          .map((pdfPage) => {
            const pageIsSelected = this.props.selectedPage && pdfPage.id === this.props.selectedPage.id;

            return (
              <ListItemButton
                dense
                selected={pageIsSelected}
                key={pdfPage.id}
                sx={{ pl: 4 }}
                onClick={() => {
                  this.onPageClick(document.id, pdfPage.pageNumber);
                }}
              >
                <ListItemText primary={`${document.name} - Page ${pdfPage.pageNumber + 1}`} />
              </ListItemButton>
            );
          });
      }

      let pageDropdown;
      if (this.props.withPages && document.pdfPages.length > 1) {
        pageDropdown = this.state.expandedDropdowns[document.id] ? (
          <ExpandLess
            sx={{ color: 'expandList.main' }}
            onClick={(event) => this.handleShowHidePagesClick(event, document.id)}
          />
        ) : (
          <ExpandMore
            sx={{ color: 'expandList.main' }}
            onClick={(event) => this.handleShowHidePagesClick(event, document.id)}
          />
        );
      }

      const flagColor = document.componentRisk === 'High' ? '#EE5A49' : '#F4D367';
      const iconName =
        document.componentDesignId == null ? (
          <PictureAsPdfIcon />
        ) : (
          <ParaLogo width={30} height={30} viewBox="0 0 32 23" fill={flagColor} />
        );

      return (
        <div key={document.id}>
          <ListItemButton
            dense
            selected={documentIsSelected}
            onClick={() => {
              this.onDocumentClick(document.id);
            }}
            id={`document-queue-${document.id}`}
            className="document-queue-item"
          >
            <Box sx={{ minWidth: 30 }}>
              <ListItemIcon>{iconName}</ListItemIcon>
            </Box>
            <ListItemText primary={document.name} />
            {pageDropdown}
          </ListItemButton>
          <Collapse in={this.state.expandedDropdowns[document.id]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {listSubItems}
            </List>
          </Collapse>
        </div>
      );
    });

    return (
      <Box sx={{ maxWidth: 360, m: 'auto', my: 2 }}>
        <Paper elevation={1}>
          <List
            sx={{
              maxHeight: 177,
              overflowY: 'hidden',
              '&:hover': {
                overflowY: 'auto',
              },
              bgcolor: 'backContrast.main',
            }}
            id="document-queue"
          >
            {listItems}
          </List>
        </Paper>
      </Box>
    );
  }
}
